/**
 * THEME JSON CONFIG CSS VARS
 * The following default component scss variables can be overwritten via theme configuration per brand.
 * If you need to add new one, please make sure to set a default value.
 * JSON Definition for Component with name "Example": testCssVar: "20px"
 * Resulting CSS var: $example-test-css-var: 20px;
 */

$stage-default-radius: 0 !default;
$stage-default-shadow: none !default;

// margins of stage wrapper to stage (grid) container, vertical
$stage-margin-desktop-l: 88px !default;
$stage-margin-desktop: 72px !default;
$stage-margin-tablet: 64px !default;
$stage-margin-phone: 0 !default;
$stage-padding-phone: 24px !default;

// margin
$stage-margin-desktop-l-horizontal: 64px;
$stage-margin-desktop-horizontal: 40px;
$stage-margin-tablet-horizontal: 32px;
$stage-margin-phone-horizontal: 0;

// box padding
$stage-box-padding-desktop-l: 48px;
$stage-box-padding-desktop: 40px;
$stage-box-padding-tablet: 32px;
$stage-box-padding-phone: 24px;

$stage-indicator-border-radius: 0 !default;
$stage-indicator-height: 48px !default;
$stage-indicator-width: 48px !default;
$stage-indicator-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25) !default;
$stage-indicator-color-light: #ffffff !default;
$stage-indicator-color-dark: #000000 !default;

/* END THEME JSON CONFIG CSS VARS ******************************************************* */

$content-spacing: 15px;
$content-padding: 24px;
$content-width: 50%;

$grid-width-desktop-l: 1200px;
$grid-width-desktop: 1024px;
$grid-width-tablet: 704px;

:host {
  // Estimation of height of header of the page. This value will be overwritten if a real calculated
  // height is passed down as attribute to the web component.
  // --header-height: 120px;
  --header-height: 0px;

  display: block;
  font-family: $font-family-base;
}

.#{$prefix}stage {
  position: relative;
  width: 100%;
  height: auto;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 48px;
  min-height: 800px;
  padding: $stage-margin-desktop-l $stage-margin-desktop-l-horizontal;
  box-sizing: border-box;
  border-radius: $stage-default-radius;
  box-shadow: $stage-default-shadow;

  @include for-size("desktop") {
    min-height: 600px;
    padding: $stage-margin-desktop $stage-margin-desktop-horizontal;
  }

  @include for-size("tablet") {
    min-height: 480px;
    padding: $stage-margin-tablet $stage-margin-tablet-horizontal;
  }

  @include for-size("phone") {
    padding: $stage-margin-phone $stage-margin-phone-horizontal;
    display: block;
    height: initial;
  }

  .#{$prefix}video-control-wrapper {
    position: relative;
    width: 100%;
    height: auto;
    max-width: $grid-width-desktop-l;
    margin: 0 auto 0 auto;

    @include for-size(desktop) {
      max-width: $grid-width-desktop;
    }

    @include for-size(tablet) {
      max-width: $grid-width-tablet;
    }

    @include for-size("phone") {
      position: absolute;
      left: $stage-padding-phone;
      top: $stage-padding-phone;
    }
  }

  &.#{$prefix}full-height {
    max-height: unset;
    height: auto;
    min-height: calc(100vh - var(--header-height));

    @include for-size("phone") {
      height: auto;
    }

    .#{$prefix}full-height-indicator-wrapper {
      position: relative;
      height: 48px;
      margin: 0 auto 0 auto;
      cursor: pointer;

      @include for-size("phone") {
        display: none;
      }

      .#{$prefix}indicator-svg {
        border-radius: $stage-indicator-border-radius;
        height: $stage-indicator-height;
        width: $stage-indicator-width;

        &.#{$prefix}indicator-light {
          filter: drop-shadow($stage-indicator-shadow);

          .#{$prefix}indicator-path {
            transition: opacity 0.2s;
            fill: $stage-indicator-color-light;
            opacity: 0.6;
          }

          &:hover {
            .#{$prefix}indicator-path {
              fill: $stage-indicator-color-light;
              opacity: 1;
            }
          }
        }

        &.#{$prefix}indicator-dark {
          .#{$prefix}indicator-path {
            transition: opacity 0.2s;
            fill: $stage-indicator-color-dark;
            opacity: 0.6;
          }

          &:hover {
            .#{$prefix}indicator-path {
              fill: $stage-indicator-color-dark;
              opacity: 1;
            }
          }
        }
      }
    }
  }

  &.#{$prefix}auto-height {
    min-height: unset;
  }

  &.#{$prefix}has-violator {
    @include for-size("phone") {
      position: relative;
    }

    .#{$prefix}stage-violator {
      position: absolute;
      top: 0;
      right: 0;

      @include for-size("phone") {
        top: $stage-padding-phone;
        right: $stage-padding-phone;
      }
    }
  }

  .#{$prefix}stage-media {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    overflow: hidden;
    display: flex;
    flex-direction: column;

    &::before {
      display: flow-root;
      position: relative;
      @include aspect-ratio-basic-teaser(2, 1);
    }

    @include for-size("phone") {
      position: relative;

      &::before {
        @include aspect-ratio-basic-teaser(4, 3);
      }
    }
  }

  .#{$prefix}content-wrapper {
    position: relative;
    width: 100%;
    height: 100%;
    flex: 1 0 100%;
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    max-width: $grid-width-desktop-l;
    margin: 0 auto 0 auto;

    &.#{$prefix}bottom {
      align-items: flex-end;
    }

    @include for-size(desktop) {
      max-width: $grid-width-desktop;
    }

    @include for-size(tablet) {
      max-width: $grid-width-tablet;
    }

    @include for-size(phone) {
      width: inherit;
      margin: 0;
      position: static;
    }
  }

  .#{$prefix}stage-content {
    display: flex;
    flex-direction: column;
    flex: calc($content-width - 24px) 0 1;
    box-sizing: border-box;
    align-items: flex-start;
    margin-left: unset;
    margin-right: auto;
    background: v(backgroundColor);
    padding: $stage-box-padding-desktop-l;

    @include for-size(desktop) {
      flex: calc($content-width - 24px) 0 1;
      padding: $stage-box-padding-desktop;
    }

    @include for-size(tablet) {
      flex: calc($content-width - 20px) 0 1;
      padding: $stage-box-padding-tablet;
    }

    @include for-size(phone) {
      padding: $stage-box-padding-phone;
      flex: 100% 0 0;
    }

    &.#{$prefix}gradient {
      background: v(gradientColor);
    }

    &.#{$prefix}transparent-background {
      @include for-size(tablet-or-larger) {
        padding: 0;
        background: transparent;
      }
    }

    .#{$prefix}stage-ctas {
      display: flex;
      align-items: center;
      flex-direction: row;
      justify-content: flex-start;
      margin-top: 33px;
      gap: 16px;
      flex-wrap: wrap;

      @include for-size(phone) {
        width: auto;
        flex-wrap: wrap;
        margin: 17px 0 0;
      }

      @include for-size(tablet) {
        margin-top: 25px;
      }
    }

    & > *:not(:last-child) {
      margin-bottom: $content-spacing;
    }
  }
}
